import { useEffect, useId, useRef, type ReactNode } from 'react';
import './ModalWindow.scss';

/**
 * Every open ModalWindow, outermost first. Escape belongs to the topmost one
 * only: modals stack (a confirmation over a dialog), and a plain document
 * listener per modal would otherwise let the *first* registered one — the
 * outer — consume the key and close out from under the inner.
 *
 * Module-level rather than context: each modal renders inline, wherever its
 * owner sits in the tree, so two stacked modals are usually not in each
 * other's React subtree and no provider could see both.
 */
const openModals: Array<object> = [];

export interface ModalButton {
  label: ReactNode;
  onClick: () => void;
  className?: string;
  disabled?: boolean;
  /** Enter activates this button while the modal is topmost. */
  primary?: boolean;
  'data-testid'?: string;
}

// Page scroll is locked with Bulma's `is-clipped` on <html> while a modal is
// open. That lock is a single shared flag but modals STACK (a modal can open
// another), so it is refcounted here at module level: the class goes on when
// the first modal opens and comes off only when the last one closes. Without
// the count, closing the upper of two stacked modals unlocked the page behind
// the one still open.
let openModalCount = 0;

const lockPageScroll = () => {
  openModalCount += 1;
  if (openModalCount === 1) {
    document.documentElement.classList.add('is-clipped');
  }
};

const unlockPageScroll = () => {
  openModalCount = Math.max(0, openModalCount - 1);
  if (openModalCount === 0) {
    document.documentElement.classList.remove('is-clipped');
  }
};

export interface ModalWindowProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  buttons?: ModalButton[];
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  /** On mobile, slide up from the bottom of the screen instead of docking to the top. Default false. */
  mobileBottomSheet?: boolean;
  /**
   * Escape closes the modal unless this is set to `false`. A child that
   * handles Escape itself must call `preventDefault` on the event so it
   * isn't also closed here.
   */
  closeOnEscape?: boolean;
  /**
   * Enter's action for a modal whose commit button lives in the body rather
   * than in `buttons` (where `primary: true` marks it instead). Only called
   * while the modal is topmost; pass `undefined` whenever the commit is not
   * currently allowed, since there is no button here to read `disabled` off.
   */
  onPrimary?: () => void;
}

const ModalWindow = ({
  isOpen,
  onClose,
  title,
  children,
  buttons = [],
  size = 'medium',
  mobileBottomSheet = false,
  closeOnEscape = true,
  onPrimary,
}: ModalWindowProps) => {
  const titleId = useId();
  // Hold one reference on the shared page-scroll lock for as long as this
  // modal is open; the cleanup covers both closing (isOpen -> false) and
  // unmounting while open.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    lockPageScroll();
    return unlockPageScroll;
  }, [isOpen]);

  // Identity of this modal in `openModals`. A ref so it survives re-renders
  // and the two effects below agree on which entry is ours.
  const escapeToken = useRef({});

  // Stack membership, deliberately keyed on `isOpen` alone: a `closeOnEscape`
  // or `onClose` change must not pop and re-push the token, which would
  // reorder the stack under a modal that is already open. A modal that does
  // not itself answer Escape still takes part, so it shields the one below it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const token = escapeToken.current;
    openModals.push(token);
    return () => {
      const at = openModals.lastIndexOf(token);
      if (at >= 0) openModals.splice(at, 1);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) return undefined;
    const token = escapeToken.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      // Topmost modal only — the others keep their listener but stay quiet.
      if (openModals[openModals.length - 1] !== token) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEscape, onClose]);

  // What Enter commits, read through a ref so the listener below is registered
  // once per open instead of re-registered on every render (the `buttons` array
  // is a fresh literal at nearly every call site).
  const primaryRef = useRef<{ buttons: ModalButton[]; onPrimary?: () => void }>({ buttons, onPrimary });
  useEffect(() => { primaryRef.current = { buttons, onPrimary }; });

  // Enter confirms the topmost modal — registered like the Escape listener
  // above, and for the same reason: stacked modals would otherwise all answer
  // the key. Not gated on `closeOnEscape`, which is about closing, not
  // confirming.
  useEffect(() => {
    if (!isOpen) return undefined;
    const token = escapeToken.current;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.defaultPrevented) return;
      // Enter with a modifier is never a plain confirm (Shift+Enter is a
      // newline, Ctrl/Alt/Meta belong to the browser or the focused control).
      if (e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;
      if (openModals[openModals.length - 1] !== token) return;
      // Controls that own Enter themselves: a newline in a textarea, the
      // native listbox behaviour of a select, a line break in rich text.
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'textarea' || tag === 'select') return;
      const editable = target?.getAttribute?.('contenteditable');
      if (editable != null && editable !== 'false') return;
      // A focused control the browser activates on Enter keeps the key: Cancel
      // (or the header X) must cancel, not confirm, and the primary button
      // when it has focus — which is where `useDialogFocus` parks it — fires
      // once through its own native activation rather than twice.
      if (target?.closest?.('button, a[href], input[type="submit"], input[type="button"]')) return;

      const primary = primaryRef.current.buttons.find((b) => b.primary);
      if (primary) {
        if (primary.disabled) return;
        e.preventDefault();
        primary.onClick();
        return;
      }
      const fallback = primaryRef.current.onPrimary;
      if (!fallback) return;
      e.preventDefault();
      fallback();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen]);

  // Where focus goes when this modal closes: whatever had it on the render
  // that opened the modal. Captured during that render rather than in an
  // effect, because a child's mount effect runs *before* this component's —
  // by then the dialogs have already focused their own body and
  // `document.activeElement` is inside the modal.
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  if (isOpen && !wasOpenRef.current) {
    const active = document.activeElement;
    // <body> means nothing had focus — there is nothing to give back.
    openerRef.current = active instanceof HTMLElement && active !== document.body ? active : null;
  }
  wasOpenRef.current = isOpen;

  useEffect(() => {
    if (!isOpen) return undefined;
    return () => {
      // Deliberately not cleared: in StrictMode this cleanup also runs during
      // the mount-time setup→cleanup→setup, and a modal mounted already open
      // (every manager dialog passes a literal `isOpen`) would lose its opener
      // before it ever closed. The capture above overwrites it on each
      // closed→open edge anyway.
      const opener = openerRef.current;
      if (!opener) return;
      // In a microtask, so it lands after the stack pop above and after the
      // rest of this commit's effects: a modal still open below this one, or a
      // page that moves focus itself as a dialog closes (the album manager
      // puts it back on the active panel's rows, which keeps its F-keys
      // alive), gets the first say. Only focus that nothing else claimed —
      // dropped to <body> when the modal's own focused node unmounted — is
      // handed back to the opener.
      queueMicrotask(() => {
        if (!opener.isConnected) return;
        const active = document.activeElement;
        if (active !== null && active !== document.body) return;
        opener.focus();
      });
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={onClose}></div>
      <div
        className={`modal-card modal-${size}${mobileBottomSheet ? ' modal-bottom-sheet' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="modal-card-head">
          <p className="modal-card-title" id={titleId} role="heading" aria-level={2}>{title}</p>
          <button
            className="delete"
            aria-label="close"
            onClick={onClose}
          ></button>
        </header>
        <section className="modal-card-body">
          {children}
        </section>
        {buttons.length > 0 && (
          <footer className="modal-card-foot">
            {buttons.map((button, index) => (
              <button
                key={index}
                className={button.className || 'button'}
                onClick={button.onClick}
                disabled={button.disabled}
                data-testid={button['data-testid']}
              >
                {button.label}
              </button>
            ))}
          </footer>
        )}
      </div>
    </div>
  );
};

export default ModalWindow;
